import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { companiesAPI, cardsAPI, leadsAPI, inquiriesAPI, dashboardAPI, settingsAPI } from '@/lib/api';
import { useSettings as useGlobalSettings } from '@/context/SettingsContext';
import { toast } from 'sonner';
import {
  Building2, CreditCard, Users, Activity, Search,
  Download, Plus, Shield, AlertTriangle,
  Eye, Edit, LogIn, Lock
} from 'lucide-react';

export default function AdminPage() {
  const { isAuthenticated, login, logout, user, isSuperAdmin } = useAuth();
  const [email, setEmail] = useState('admin@theace.co.il');
  const [password, setPassword] = useState('TheAce2025!');
  const [loginLoading, setLoginLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [stats, setStats] = useState({ companies: 0, cards: 0, leads: 0, expiring: 0, inquiries: 0 });
  const [companies, setCompanies] = useState([]);
  const [cards, setCards] = useState([]);
  const [leads, setLeads] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Add company dialog
  const [showAddCompany, setShowAddCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');

  // Edit card dialog with photo upload
  const [showEditCard, setShowEditCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const fetchData = useCallback(async () => {
    setDataLoading(true);
    try {
      const [statsRes, compRes, cardsRes, leadsRes, inqRes] = await Promise.all([
        dashboardAPI.stats(),
        companiesAPI.list(),
        cardsAPI.list(),
        leadsAPI.list(),
        inquiriesAPI.list(),
      ]);
      setStats(statsRes.data);
      setCompanies(compRes.data);
      setCards(cardsRes.data);
      setLeads(leadsRes.data);
      setInquiries(inqRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [isAuthenticated, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back, Admin!');
    } catch (err) {
      toast.error('Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAddCompany = async () => {
    if (!newCompanyName.trim()) return;
    try {
      await companiesAPI.create({ name: newCompanyName.trim() });
      toast.success('Company created!');
      setShowAddCompany(false);
      setNewCompanyName('');
      fetchData();
    } catch (err) {
      toast.error('Failed to create company');
    }
  };

  const handleExportCSV = () => {
    const token = localStorage.getItem('ace_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/leads/export`;
    // Create a link with auth header via fetch
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'leads_export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success('CSV exported!');
      })
      .catch(() => toast.error('Export failed'));
  };

  const handleDeleteCompany = async (id) => {
    try {
      await companiesAPI.remove(id);
      toast.success('Company deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleEditCard = (card) => {
    setSelectedCard(card);
    setShowEditCard(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('ace_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      const photoUrl = data.url;

      // Update card with new photo URL
      await cardsAPI.update(selectedCard.id, { photo_url: photoUrl });
      
      toast.success('Photo uploaded successfully!');
      setShowEditCard(false);
      fetchData();
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="pt-24 pb-20 flex items-center justify-center min-h-screen">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm mx-auto px-4"
        >
          <Card className="bg-card border-border">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 border border-primary/20">
                  <Lock className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-title">Admin Access</h2>
                <p className="text-sm text-body mt-1">Sign in to manage The Ace platform</p>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-sm text-title mb-2 block">Email</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} className="bg-secondary border-border text-title" type="email" />
                </div>
                <div>
                  <Label className="text-sm text-title mb-2 block">Password</Label>
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} className="bg-secondary border-border text-title" type="password" />
                </div>
                <Button type="submit" disabled={loginLoading} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 gap-2 shadow-cyan">
                  <LogIn className="w-4 h-4" />
                  {loginLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              <p className="text-xs text-body text-center mt-4">Credentials: admin@theace.co.il / TheAce2025!</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Filter data by search
  const filteredCompanies = companies.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  const filteredCards = cards.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()) || (c.title || '').toLowerCase().includes(search.toLowerCase()));
  const filteredLeads = leads.filter((l) => (l.guest_name || '').toLowerCase().includes(search.toLowerCase()) || (l.guest_email || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8"
        >
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-title">Admin Dashboard</h1>
            </div>
            <p className="text-sm text-body">Welcome, {user?.name} ({user?.role === 'super_admin' ? 'Super Admin' : 'Client Master'})</p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-body" />
              <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64 bg-secondary border-border text-title" />
            </div>
            {isSuperAdmin && (
              <Button onClick={() => setShowAddCompany(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 shadow-cyan">
                <Plus className="w-4 h-4" /> Add Company
              </Button>
            )}
            <Button variant="outline" onClick={logout} className="border-border text-title hover:bg-secondary">Logout</Button>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Companies', value: stats.companies, icon: Building2, color: 'primary' },
            { label: 'Active Cards', value: stats.cards, icon: CreditCard, color: 'primary' },
            { label: 'Total Leads', value: stats.leads, icon: Users, color: 'primary' },
            { label: 'Expiring Soon', value: stats.expiring, icon: AlertTriangle, color: 'warning' },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.label} className="bg-card border-border">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color === 'warning' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-title">{stat.value}</p>
                    <p className="text-xs text-body">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="companies" className="w-full">
          <TabsList className="bg-secondary h-11 w-full sm:w-auto">
            <TabsTrigger value="companies" className="gap-2 text-sm data-[state=active]:bg-card data-[state=active]:text-primary"><Building2 className="w-4 h-4" /> Companies</TabsTrigger>
            <TabsTrigger value="cards" className="gap-2 text-sm data-[state=active]:bg-card data-[state=active]:text-primary"><CreditCard className="w-4 h-4" /> Cards</TabsTrigger>
            <TabsTrigger value="leads" className="gap-2 text-sm data-[state=active]:bg-card data-[state=active]:text-primary"><Users className="w-4 h-4" /> Leads</TabsTrigger>
            <TabsTrigger value="inquiries" className="gap-2 text-sm data-[state=active]:bg-card data-[state=active]:text-primary"><Activity className="w-4 h-4" /> Inquiries</TabsTrigger>
            <TabsTrigger value="settings" className="gap-2 text-sm data-[state=active]:bg-card data-[state=active]:text-primary"><Shield className="w-4 h-4" /> הגדרות אתר</TabsTrigger>
          </TabsList>

          <TabsContent value="companies" className="mt-6">
            <Card className="bg-card border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-body">Company</TableHead>
                    <TableHead className="text-body">Cards</TableHead>
                    <TableHead className="text-body">Leads</TableHead>
                    <TableHead className="text-body">Created</TableHead>
                    {isSuperAdmin && <TableHead className="text-body text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((company) => (
                    <TableRow key={company.id} className="border-border hover:bg-secondary/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                            {company.name.split(' ').map(w => w[0]).join('').slice(0,2)}
                          </div>
                          <span className="text-sm font-medium text-title">{company.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-title">{company.card_count || cards.filter(c => c.company_id === company.id).length}</TableCell>
                      <TableCell className="text-sm text-title">{company.lead_count || 0}</TableCell>
                      <TableCell className="text-sm text-body">{new Date(company.created_at).toLocaleDateString()}</TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-body hover:text-title" onClick={() => toast.info('Edit coming soon')}><Edit className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-destructive/60 hover:text-destructive" onClick={() => handleDeleteCompany(company.id)}><AlertTriangle className="w-4 h-4" /></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="cards" className="mt-6">
            <Card className="bg-card border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-body">Name</TableHead>
                    <TableHead className="text-body">Title</TableHead>
                    <TableHead className="text-body">Slug</TableHead>
                    <TableHead className="text-body">Views</TableHead>
                    <TableHead className="text-body">Expiry</TableHead>
                    <TableHead className="text-body text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCards.map((card) => {
                    const isExpiring = new Date(card.expiry_date) < new Date(Date.now() + 14 * 86400000);
                    return (
                      <TableRow key={card.id} className="border-border hover:bg-secondary/50">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">{card.name[0]}</div>
                            <span className="text-sm font-medium text-title">{card.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-body">{card.title}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px] border-primary/20 text-primary">/c/{card.slug}</Badge></TableCell>
                        <TableCell className="text-sm text-title">{card.views}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${isExpiring ? 'border-warning/30 text-warning bg-warning/5' : 'border-success/30 text-success bg-success/5'}`}>
                            {new Date(card.expiry_date).toLocaleDateString()}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-body hover:text-title" onClick={() => handleEditCard(card)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="w-8 h-8 text-body hover:text-title" onClick={() => window.open(`/c/${card.slug}`, '_blank')}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="leads" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-body">Real-time lead tracking ({leads.length} total)</p>
              <Button variant="outline" size="sm" className="gap-2 border-border text-title hover:bg-secondary" onClick={handleExportCSV}>
                <Download className="w-4 h-4" /> Export CSV
              </Button>
            </div>
            <Card className="bg-card border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-body">שם האורח</TableHead>
                    <TableHead className="text-body">חברה</TableHead>
                    <TableHead className="text-body">תפקיד</TableHead>
                    <TableHead className="text-body">טלפון</TableHead>
                    <TableHead className="text-body">אימייל</TableHead>
                    <TableHead className="text-body">הודעה</TableHead>
                    <TableHead className="text-body">תאריך</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow key={lead.id} className="border-border hover:bg-secondary/50">
                      <TableCell className="text-sm font-medium text-title">{lead.guest_name || 'Anonymous'}</TableCell>
                      <TableCell className="text-sm text-body">{lead.company_name || '—'}</TableCell>
                      <TableCell className="text-sm text-body">{lead.job_title || '—'}</TableCell>
                      <TableCell className="text-sm text-body">{lead.phone_number || lead.guest_phone || '—'}</TableCell>
                      <TableCell className="text-sm text-body">{lead.guest_email || '—'}</TableCell>
                      <TableCell className="text-xs text-body max-w-[200px] truncate">{lead.notes || '—'}</TableCell>
                      <TableCell className="text-sm text-body">{new Date(lead.timestamp).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <Card className="bg-card border-border">
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-body">Customer</TableHead>
                    <TableHead className="text-body">Contact</TableHead>
                    <TableHead className="text-body">Material</TableHead>
                    <TableHead className="text-body">Style</TableHead>
                    <TableHead className="text-body">Consent</TableHead>
                    <TableHead className="text-body">Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((inq) => (
                    <TableRow key={inq.id} className="border-border hover:bg-secondary/50">
                      <TableCell className="text-sm font-medium text-title">{inq.customer_name}</TableCell>
                      <TableCell className="text-sm text-body">{inq.contact}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px] border-border text-body">{inq.material_type}</Badge></TableCell>
                      <TableCell className="text-sm text-title capitalize">{inq.style || '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${inq.marketing_consent ? 'border-success/30 text-success bg-success/5' : 'border-border text-body'}`}>
                          {inq.marketing_consent ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-body">{new Date(inq.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <SettingsPanel />
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Company Dialog */}
      <Dialog open={showAddCompany} onOpenChange={setShowAddCompany}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-title">Add New Company</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-sm text-title mb-2 block">Company Name</Label>
            <Input value={newCompanyName} onChange={(e) => setNewCompanyName(e.target.value)} className="bg-secondary border-border text-title" placeholder="Acme Corp" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCompany(false)} className="border-border text-title hover:bg-secondary">Cancel</Button>
            <Button onClick={handleAddCompany} className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-silver">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Card Dialog with Photo Upload */}
      <Dialog open={showEditCard} onOpenChange={setShowEditCard}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-title">Upload Card Photo</DialogTitle>
          </DialogHeader>
          {selectedCard && (
            <div className="py-4 space-y-4">
              <div>
                <p className="text-sm text-body mb-1">Card Owner</p>
                <p className="text-base font-medium text-title">{selectedCard.name}</p>
                <p className="text-xs text-body">{selectedCard.title}</p>
              </div>
              
              <Separator className="bg-border" />
              
              {selectedCard.photo_url && (
                <div className="mb-4">
                  <p className="text-sm text-body mb-2">Current Photo:</p>
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-border">
                    <img 
                      src={selectedCard.photo_url} 
                      alt={selectedCard.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label className="text-sm text-title mb-2 block">
                  Upload New Portrait Photo
                </Label>
                <Input 
                  type="file" 
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                  className="bg-secondary border-border text-title file:mr-4 file:px-4 file:py-2 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:text-xs file:font-medium hover:file:bg-primary/90"
                />
                <p className="text-xs text-body mt-2">
                  Max 5MB. Recommended: 400x400px square portrait.
                </p>
              </div>
              
              {uploadingPhoto && (
                <div className="flex items-center gap-2 text-sm text-primary">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowEditCard(false)} 
              className="border-border text-title hover:bg-secondary"
              disabled={uploadingPhoto}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════
// Settings Panel Component
// ═══════════════════════════════════════════
function SettingsPanel() {
  const { refreshSettings } = useGlobalSettings();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await settingsAPI.getAll();
      setSettings(res.data);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (key) => {
    setSaving(key);
    try {
      await settingsAPI.update(key, settings[key]);
      toast.success('הגדרה עודכנה בהצלחה');
      // Refresh global settings to update all components
      refreshSettings();
    } catch (err) {
      toast.error('שגיאה בעדכון');
    } finally {
      setSaving('');
    }
  };

  const settingFields = [
    { key: 'site_title', label: 'כותרת האתר', placeholder: 'The Ace - Art, Events, Studio', description: 'יוצג בכרטיסיית הדפדפן' },
    { key: 'contact_phone', label: 'טלפון ליצירת קשר', placeholder: '055-9995685', description: 'מוצג בכל רחבי האתר' },
    { key: 'contact_email', label: 'אימייל ליצירת קשר', placeholder: 'studio@theace.co.il', description: 'מוצג בפוטר ובדפים' },
    { key: 'hero_subtitle', label: 'כותרת משנה בדף הבית', placeholder: 'Precision laser engraving...', description: 'הטקסט המרכזי בראש הדף' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h2 className="text-xl font-bold text-title mb-2">הגדרות אתר גלובליות</h2>
        <p className="text-sm text-body">עדכן את המידע הבסיסי של האתר. השינויים ייכנסו לתוקף מיידית בכל האתר.</p>
      </div>

      <div className="grid gap-6">
        {settingFields.map((field) => (
          <Card key={field.key} className="bg-card border-border">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-1 space-y-3">
                  <div>
                    <Label className="text-sm font-semibold text-title">{field.label}</Label>
                    <p className="text-xs text-body mt-1">{field.description}</p>
                  </div>
                  <Input
                    value={settings[field.key] || ''}
                    onChange={(e) => setSettings({ ...settings, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    className="bg-secondary border-border text-title"
                  />
                </div>
                <Button
                  onClick={() => handleUpdate(field.key)}
                  disabled={saving === field.key}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-silver sheen-effect laser-engraved mt-7"
                >
                  {saving === field.key ? 'שומר...' : 'עדכן'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="bg-accent/5 border-accent/20">
        <CardContent className="p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-accent mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-accent mb-1">שליטה מלאה על האתר</p>
            <p className="text-body leading-relaxed">כל שינוי שמתבצע כאן משתקף מיידית בכל דפי האתר - כולל הניווט, הפוטר, והעמודים הראשיים. אין צורך בשינויים טכניים או עדכוני קוד.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
